import { createContext, useContext } from "react";
import { createStore, useStore, type StoreApi } from "zustand";

export interface CheckoutFormState {
  businessJustification: string;
  costCenter: string;
  carCode: string;
  serialNumberForReturnDevice: string;
  managerConfirmationCheckbox: boolean;
  shippingDetailsConfirmationCheckbox: boolean;
  policyConfirmationCheckbox: boolean;
  deliveryInstructions: string;
  fullName: string;
  country: string;
  countryISO3: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  citySubdivision1: string;
  postalCode: string;
  countryCode: string;
  phoneNumber: string;
}

export type { ValidationErrors } from "@/pages/checkout/validation/checkout-validation";

export type FormValue = string | boolean;

export interface CountrySelection {
  name: string;
  iso3: string;
  phonecode: string;
}

export interface CheckoutStore {
  form: CheckoutFormState;
  hasSubmitAttempted: boolean;
  updateField: (field: keyof CheckoutFormState, value: FormValue) => void;
  selectCountry: (countryName: string, selection?: CountrySelection) => void;
  selectState: (stateName: string) => void;
  markSubmitAttempted: () => void;
}

const initialFormState: CheckoutFormState = {
  businessJustification: "",
  costCenter: "",
  carCode: "",
  serialNumberForReturnDevice: "",
  managerConfirmationCheckbox: false,
  shippingDetailsConfirmationCheckbox: false,
  policyConfirmationCheckbox: false,
  deliveryInstructions: "",
  fullName: "",
  country: "",
  countryISO3: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  citySubdivision1: "",
  postalCode: "",
  countryCode: "",
  phoneNumber: "",
};

export const createCheckoutStore = () =>
  createStore<CheckoutStore>((set) => ({
    form: initialFormState,
    hasSubmitAttempted: false,
    updateField: (field, value) =>
      set((state) => ({ form: { ...state.form, [field]: value } })),
    selectCountry: (countryName, selection) =>
      set((state) => ({
        form: {
          ...state.form,
          country: countryName,
          citySubdivision1: "",
          city: "",
          countryCode: selection?.phonecode ?? "",
          countryISO3: selection?.iso3 ?? "",
        },
      })),
    selectState: (stateName) =>
      set((state) => ({
        form: {
          ...state.form,
          citySubdivision1: stateName,
          city: "",
        },
      })),
    markSubmitAttempted: () =>
      set({ hasSubmitAttempted: true }),
  }));

export const CheckoutStoreContext = createContext<StoreApi<CheckoutStore> | null>(null);

export const useCheckoutStore = <T>(selector: (state: CheckoutStore) => T): T => {
  const store = useContext(CheckoutStoreContext);
  if (!store) {
    throw new Error("useCheckoutStore must be used within a CheckoutStoreProvider");
  }
  return useStore(store, selector);
};

export const useCheckoutStoreApi = (): StoreApi<CheckoutStore> => {
  const store = useContext(CheckoutStoreContext);
  if (!store) {
    throw new Error("useCheckoutStoreApi must be used within a CheckoutStoreProvider");
  }
  return store;
};
